import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ArrowBack, Add } from '@mui/icons-material';
import { couponBatchService, CreateCouponBatchRequest } from '../services/couponBatchService';
import { campaignService } from '../services/campaignService';
import { useAuth } from '../contexts/AuthContext';
import { AppHeader } from '../components/AppHeader';
import { Campaign, Store } from '../types';

interface CouponBatchFormData {
  startNumber: number;
  endNumber: number;
  campaignId: string;
  storeId: string;
}

const couponBatchSchema: yup.ObjectSchema<CouponBatchFormData> = yup.object({
  startNumber: yup.number()
    .required('Número inicial é obrigatório')
    .min(1, 'Número inicial deve ser maior que zero'),
  endNumber: yup.number()
    .required('Número final é obrigatório')
    .min(1, 'Número final deve ser maior que zero')
    .test('is-greater', 'Número final deve ser maior que o inicial', function(value) {
      return value > this.parent.startNumber;
    }),
  campaignId: yup.string().required('Campanha é obrigatória'),
  storeId: yup.string().required('Loja é obrigatória')
});

  const CouponBatchForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingStores, setLoadingStores] = useState(true);
  const isEdit = !!id;

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<CouponBatchFormData>({
    resolver: yupResolver(couponBatchSchema),
    defaultValues: {
      startNumber: 1,
      endNumber: 100,
      campaignId: '',
      storeId: ''
    }
  });

  const startNumber = watch('startNumber');
  const endNumber = watch('endNumber');
  const campaignId = watch('campaignId');
  const storeId = watch('storeId');
  const quantity = endNumber && startNumber ? endNumber - startNumber + 1 : 0;

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoadingCampaigns(true);
      const data = await campaignService.getCampaignsByAssociation();
      setCampaigns(data);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  // Lojas participantes da campanha selecionada (não todas as lojas do sistema)
  const fetchStoresForCampaign = useCallback(async (selectedCampaignId: string) => {
    if (!selectedCampaignId) {
      setStores([]);
      return;
    }
    try {
      setLoadingStores(true);
      const campaign = await campaignService.getCampaignById(selectedCampaignId);
      setStores(campaign.stores || []);
    } catch (error) {
      console.error('Erro ao carregar lojas da campanha:', error);
      setStores([]);
    } finally {
      setLoadingStores(false);
    }
  }, []);

  const fetchCouponBatch = useCallback(async (couponBatchId: string) => {
    try {
      console.log('Carregando lote de cupons:', couponBatchId);
      const couponBatch = await couponBatchService.getCouponBatchById(couponBatchId);
      console.log('Dados do lote carregados:', couponBatch);
      
      reset({
        startNumber: couponBatch.startNumber,
        endNumber: couponBatch.endNumber,
        campaignId: couponBatch.campaignId || couponBatch.campaign?.id || '',
        storeId: couponBatch.storeId || couponBatch.store?.id || ''
      });
      
      console.log('Formulário resetado com:', {
        startNumber: couponBatch.startNumber,
        endNumber: couponBatch.endNumber,
        campaignId: couponBatch.campaignId || couponBatch.campaign?.id,
        storeId: couponBatch.storeId || couponBatch.store?.id
      });
    } catch (error) {
      console.error('Erro ao carregar lote de cupons:', error);
      setError('Erro ao carregar dados do lote de cupons');
    }
  }, [reset]);

  useEffect(() => {
    fetchCampaigns();

    if (isEdit && id) {
      fetchCouponBatch(id);
    }
  }, [isEdit, id]);

  // Recarrega as lojas participantes sempre que a campanha selecionada mudar,
  // em vez de listar todas as lojas do sistema.
  useEffect(() => {
    fetchStoresForCampaign(campaignId);
  }, [campaignId, fetchStoresForCampaign]);

  // Se a loja selecionada não pertence mais à campanha (troca de campanha),
  // limpa a seleção para evitar enviar uma loja fora da campanha.
  useEffect(() => {
    if (!loadingStores && storeId && !stores.some((store) => store.id === storeId)) {
      setValue('storeId', '');
    }
  }, [stores, loadingStores, storeId, setValue]);

  const onSubmit: SubmitHandler<CouponBatchFormData> = async (data) => {
    try {
      setLoading(true);
      setError('');

      const requestData: CreateCouponBatchRequest = {
        startNumber: data.startNumber,
        endNumber: data.endNumber,
        campaignId: data.campaignId,
        storeId: data.storeId
      };

      if (isEdit && id) {
        await couponBatchService.updateCouponBatch(id, requestData);
      } else {
        await couponBatchService.createCouponBatch(requestData);
      }

      navigate('/coupon-batches');
    } catch (err: any) {
      console.error('Erro ao salvar:', err);
      
      // Tratar diferentes tipos de erro
      if (err.response?.status === 409) {
        // Conflito de intervalo
        setError(err.response.data.message || 'Conflito de intervalo detectado');
      } else if (err.response?.status === 400) {
        // Dados inválidos
        setError(err.response.data.message || 'Dados inválidos fornecidos');
      } else if (err.response?.status === 404) {
        // Recurso não encontrado
        setError('Campanha ou loja não encontrada');
      } else {
        // Erro genérico
        setError(err.response?.data?.message || 'Erro ao salvar lote de cupons');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <AppHeader 
        title={isEdit ? 'Editar Lote de Cupons' : 'Novo Lote de Cupons'}
        subtitle={isEdit ? 'Atualize as informações do lote' : 'Crie um novo lote de cupons'}
        icon={<Add />}
        backTo="/coupon-batches"
      />

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            {isEdit ? 'Editar Lote de Cupons' : 'Novo Lote de Cupons'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              {/* Informações do Lote */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Informações do Lote de Cupons
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <Controller
                name="campaignId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.campaignId}>
                    <InputLabel id="campaign-label">Campanha</InputLabel>
                    <Select
                      {...field}
                      labelId="campaign-label"
                      label="Campanha"
                      disabled={loadingCampaigns}
                    >
                      {loadingCampaigns ? (
                        <MenuItem disabled>Carregando campanhas...</MenuItem>
                      ) : campaigns.length === 0 ? (
                        <MenuItem disabled>Nenhuma campanha encontrada</MenuItem>
                      ) : (
                        campaigns.map((campaign) => (
                          <MenuItem key={campaign.id} value={campaign.id}>
                            {campaign.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {errors.campaignId && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.campaignId.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="storeId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.storeId}>
                    <InputLabel id="store-label">Loja</InputLabel>
                    <Select
                      {...field}
                      labelId="store-label"
                      label="Loja"
                      disabled={loadingStores}
                    >
                      {loadingStores ? (
                        <MenuItem disabled>Carregando lojas...</MenuItem>
                      ) : stores.length === 0 ? (
                        <MenuItem disabled>Nenhuma loja encontrada</MenuItem>
                      ) : (
                        stores.map((store) => (
                          <MenuItem key={store.id} value={store.id}>
                            {store.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {errors.storeId && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.storeId.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Controller
                    name="startNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Número Inicial"
                        type="number"
                        error={!!errors.startNumber}
                        helperText={errors.startNumber?.message}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Controller
                    name="endNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Número Final"
                        type="number"
                        error={!!errors.endNumber}
                        helperText={errors.endNumber?.message}
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Informação da Quantidade */}
              {quantity > 0 && (
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'primary.light', 
                  borderRadius: 1,
                  color: 'primary.contrastText'
                }}>
                  <Typography variant="h6" align="center">
                    Total de Cupons: {quantity}
                  </Typography>
                </Box>
              )}

              {/* Botões de Ação */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/coupon-batches')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : (isEdit ? 'Atualizar' : 'Criar')}
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>
      </Container>
    </>
  );
}; 
export default CouponBatchForm;